import {
  Button,
  Heading,
  IconButton,
  IconType,
} from '@assembly-js/design-system';
import clsx from 'clsx';
import {
  createContext,
  Dispatch,
  FC,
  MouseEventHandler,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { CopyToClipboard } from 'src/components/CopyToClipboard';
import { addCustomCopyBehavior } from 'src/utils/ClipboardUtils';

export const widgetButtonStyles =
  'text-gray-small bg-gray-100 border-non-hover-border hover:bg-secondary-hover disabled:border-gray-150 disabled:text-gray-300';

interface WidgetContextType {
  truncateDraft: boolean;
  setTruncateDraft: Dispatch<SetStateAction<boolean>>;
}

const WidgetContext = createContext<WidgetContextType>({
  truncateDraft: true,
  setTruncateDraft: () => {},
});

export const WidgetContainer: FC<{ className?: string }> = ({
  className,
  children,
}) => {
  const [truncateDraft, setTruncateDraft] = useState(true);

  return (
    <WidgetContext.Provider value={{ truncateDraft, setTruncateDraft }}>
      <div
        className={clsx(
          'border border-solid border-non-hover-border rounded-lg bg-gray-100 flex flex-col mt-3',
          truncateDraft && 'max-h-[448px]',
          className,
        )}
      >
        {children}
      </div>
    </WidgetContext.Provider>
  );
};

export const WidgetHeader = ({
  widgetName,
  buttonName,
  buttonIcon,
  buttonDisabled,
  onClick,
  copyIcon,
  copyLink,
}: {
  widgetName: string;
  buttonName: string;
  buttonIcon: IconType;
  buttonDisabled?: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  copyIcon?: IconType;
  copyLink?: string;
}) => (
  <div className="pt-3 pb-2 px-4 border-l-0 border-r-0 border-t-0 border-b border-solid border-non-hover-border flex items-center justify-between flex-shrink-0">
    <Heading className="text-gray-small" size="sm">
      {widgetName}
    </Heading>
    <div className="flex gap-2 items-center">
      {copyIcon && (
        <CopyToClipboard>
          {({ copy }) => (
            <IconButton
              variant="minimal"
              size="sm"
              onClick={() => copy(copyLink)}
              icon={copyIcon}
              className="text-gray-small"
            />
          )}
        </CopyToClipboard>
      )}
      <Button
        className={clsx(
          'py-px [&>svg]:p-0 [&>svg]:mr-2 select-none',
          widgetButtonStyles,
        )}
        prefixIcon={buttonIcon}
        size="sm"
        variant="secondary"
        label={buttonName}
        onClick={onClick}
        disabled={buttonDisabled}
        {...addCustomCopyBehavior({ excludeFromCopy: true })}
      />
    </div>
  </div>
);

export const WidgetContent: FC = ({ children }) => (
  <div className="px-4 py-3 flex-1 min-h-0 flex flex-col">{children}</div>
);

export const WidgetDraft: FC = ({ children }) => {
  const { truncateDraft, setTruncateDraft } = useContext(WidgetContext);
  const [isTruncated, setIsTruncated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const checkIsTruncated = () => {
      if (ref.current) {
        const { offsetHeight, scrollHeight } = ref.current;
        if (offsetHeight < scrollHeight) {
          setIsTruncated(true);
        } else {
          setIsTruncated(false);
        }
      }
    };
    const resizeObserver = new ResizeObserver(checkIsTruncated);

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    checkIsTruncated();

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <>
      <div
        className={clsx(
          'flex-1 min-h-0 whitespace-pre-wrap',
          truncateDraft && 'overflow-hidden',
        )}
        ref={ref}
      >
        {children}
      </div>
      {isTruncated && truncateDraft && (
        <div className="flex-shrink-0">
          <Button
            className="bg-none hover:underline p-0 text-gray-small"
            onClick={() => setTruncateDraft(false)}
            label="Show more"
            variant="text"
          />
        </div>
      )}
    </>
  );
};

export const WidgetItem: FC<{ className?: string }> = ({
  className,
  children,
}) => <div className={clsx('flex-shrink-0', className)}>{children}</div>;
